'use client';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOpenShifts } from 'features/shift/shiftApi';
import { bookShift } from 'features/booking/bookingApi';

import Button from 'components/base/Button';
import { LocationAutocomplete } from 'components/common/LocationAutocomplete';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faClock,
  faUsers,
  faUserMd,
  faPoundSign,
  faSearch,
  faList
} from '@fortawesome/free-solid-svg-icons';

const LocumFindShiftsPage = () => {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [bookingId, setBookingId] = useState<number | null>(null);

  const [filters, setFilters] = useState({
    location: '',
    latitude: null,
    longitude: null,
    date: '',
    pay_rate: ''
  });

  // FETCH SHIFTS
  const fetchShifts = async (pageNumber = 1) => {
    try {
      setLoading(true);

      const data = await getOpenShifts({
        ...filters,
        page: pageNumber,
        limit: 6
      });

      setShifts(data.shifts || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setTotalPages(data.totalPages || 1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShifts(1);
  }, []);

  // BOOK SHIFT
  const handleBook = async (shiftId: number) => {
    try {
      setBookingId(shiftId);

      await bookShift(shiftId);

      // update UI instantly
      setShifts(prev =>
        prev.map(s => (s.id === shiftId ? { ...s, is_booked: true } : s))
      );
    } catch (err: any) {
      alert(err.message);
    } finally {
      setBookingId(null);
    }
  };

  const handleSearch = () => {
    fetchShifts(1);
  };

  return (
    <div className="container py-2">
      <h2 className="fw-bold mb-3">Find Shifts</h2>

      {/* TOTAL */}
      <span className="text-muted">
        <FontAwesomeIcon icon={faList} className="me-2" />
        {total} shifts found
      </span>

      {/* FILTER BAR */}
      <div className="card shadow-sm border-0 p-3 mb-4 mt-4">
        <div className="row g-2">
          {/* LOCATION */}
          <div className="col-md-3">
            <LocationAutocomplete
              value={filters.location}
              onChange={(location: string) =>
                setFilters(prev => ({ ...prev, location }))
              }
              onCoordinatesChange={(coords: any) =>
                setFilters(prev => ({
                  ...prev,
                  latitude: coords.lat,
                  longitude: coords.lng
                }))
              }
              placeholder="Search location"
            />
          </div>

          {/* DATE */}
          <div className="col-md-3">
            <input
              type="date"
              className="form-control"
              value={filters.date}
              onChange={e => setFilters({ ...filters, date: e.target.value })}
            />
          </div>

          {/* PAY */}
          <div className="col-md-3">
            <input
              type="number"
              placeholder="Min Pay"
              className="form-control"
              value={filters.pay_rate}
              onChange={e =>
                setFilters({ ...filters, pay_rate: e.target.value })
              }
            />
          </div>

          {/* SEARCH BUTTON */}
          <div className="col-md-3">
            <Button
              variant="phoenix-primary"
              className="w-100"
              onClick={handleSearch}
              startIcon={<FontAwesomeIcon icon={faSearch} />}
            >
              Search
            </Button>
          </div>
        </div>
      </div>

      {/* STATES */}
      {loading && <p className="text-muted">Loading shifts...</p>}
      {error && <p className="text-danger">{error}</p>}

      {!loading && shifts.length === 0 && (
        <div className="text-center text-muted mt-5">
          <h5>No shifts available</h5>
        </div>
      )}

      {/* SHIFT LIST */}
      <div className="row g-4">
        {shifts.map(shift => (
          <div className="col-md-4" key={shift.id}>
            <div className="card border-0 shadow-sm h-100 p-3 position-relative">
              {/* HEADER */}
              <div className="mb-2 position-relative">
                <h5 className="fw-bold mb-1">{shift.location}</h5>

                <span className="badge bg-primary text-capitalize">
                  <FontAwesomeIcon icon={faUserMd} className="me-1" />
                  {shift.locum_role}
                </span>

                <span className="badge bg-success position-absolute top-0 end-0">
                  {shift.available_slots} Slots
                </span>
              </div>

              {/* DATE */}
              <p className="text-muted mb-2 small">
                <FontAwesomeIcon icon={faCalendar} className="me-2" />
                {new Date(shift.date).toLocaleDateString('en-GB', {
                  weekday: 'short',
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })}
              </p>

              {/* TIME */}
              <p className="mb-2">
                <FontAwesomeIcon icon={faClock} className="me-2 text-primary" />
                {shift.start_time.slice(0, 5)} - {shift.end_time.slice(0, 5)}
              </p>

              {/* PAY + SLOTS */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-semibold">
                  <FontAwesomeIcon
                    icon={faPoundSign}
                    className="me-2 text-success"
                  />
                  {shift.pay_rate}
                </span>

                <span className="text-muted small">
                  <FontAwesomeIcon icon={faUsers} className="me-1" />
                  {shift.booked_locums_count}/{shift.required_locums}
                </span>
              </div>

              <hr />

              {/* BUTTONS */}
              <div className="d-flex gap-2">
                <Button
                  variant="secondary"
                  className="flex-grow-1"
                  onClick={() => navigate(`/dashboard/shift/${shift.id}`)}
                >
                  View Details
                </Button>

                {shift.is_booked ? (
                  <Button variant="secondary" className="flex-grow-1" disabled>
                    ✔ Booked
                  </Button>
                ) : shift.available_slots === 0 ? (
                  <Button variant="secondary" className="flex-grow-1" disabled>
                    Full
                  </Button>
                ) : (
                  <Button
                    variant="phoenix-primary"
                    className="flex-grow-1"
                    onClick={() => handleBook(shift.id)}
                    disabled={bookingId === shift.id}
                  >
                    {bookingId === shift.id ? 'Booking...' : 'Book Shift'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* PAGINATION */}
      <ul className="pagination justify-content-center mt-4">
        {[...Array(totalPages)].map((_, i) => (
          <li key={i} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
            <button className="page-link" onClick={() => fetchShifts(i + 1)}>
              {i + 1}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default LocumFindShiftsPage;
